import PropTypes from 'prop-types';

import {
  imagePropTypes,
  itinerarySideBarPropTypes,
  selectedOptionsPropTypes,
} from '../../../propTypes';
import { pricePerPaxPropTypes } from '../../../util/currency';

const contentProperties = PropTypes.shape({
  identifier: PropTypes.string,
  content: PropTypes.string,
});

const content = PropTypes.shape({
  id: PropTypes.string,
  contentType: PropTypes.string,
  properties: contentProperties,
});

const itineraryItem = PropTypes.shape({
  content: PropTypes.string,
  key: PropTypes.string,
  type: PropTypes.string,
});

const itineraryDay = PropTypes.shape({
  date: PropTypes.string,
  day: PropTypes.number,
  key: PropTypes.string,
  items: PropTypes.arrayOf(itineraryItem),
});

export const stadiumPropTypes = PropTypes.shape({
  address: PropTypes.string,
  fullDescription: PropTypes.string,
  images: PropTypes.arrayOf(imagePropTypes),
  title: PropTypes.string,
});

export const tourHotelRoomOptionPropTypes = PropTypes.shape({
  code: PropTypes.string,
  key: PropTypes.string,
  roomName: PropTypes.string,
});

export const hotelOptionPropTypes = PropTypes.shape({
  checkInTime: PropTypes.string,
  checkOutTime: PropTypes.string,
  costPerSingleSupplement: PropTypes.number,
  fullDescription: PropTypes.string,
  images: PropTypes.arrayOf(imagePropTypes),
  itineraryDescription: PropTypes.string,
  key: PropTypes.string,
  location: PropTypes.string,
  rating: PropTypes.number,
  title: PropTypes.string,
  type: PropTypes.string,
});

export const hotelTourOptionPropTypes = PropTypes.shape({
  checkInDate: PropTypes.string,
  checkOutDate: PropTypes.string,
  key: PropTypes.string,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  limitedAvailabilityRemainingText: PropTypes.string,
  option: hotelOptionPropTypes,
  pricePerPax: pricePerPaxPropTypes,
  roomTypes: PropTypes.arrayOf(tourHotelRoomOptionPropTypes),
  soldOut: PropTypes.bool,
});

export const motorhomeOptionPropTypes = PropTypes.shape({
  fullDescription: PropTypes.string,
  gearbox: PropTypes.string,
  heroBannerDescription: PropTypes.string,
  heroBannerSubtitleText: PropTypes.string,
  heroBannerTitleText: PropTypes.string,
  images: PropTypes.arrayOf(imagePropTypes),
  itineraryDescription: PropTypes.string,
  key: PropTypes.string,
  modalContent: PropTypes.arrayOf(content),
  motorhomeType: PropTypes.string,
  numberOfBirth: PropTypes.number,
  roomTypeCode: PropTypes.string,
  title: PropTypes.string,
});

export const motorhomeTourOptionPropTypes = PropTypes.shape({
  key: PropTypes.string,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  limitedAvailabilityRemainingText: PropTypes.string,
  option: motorhomeOptionPropTypes,
  price: PropTypes.number,
  soldOut: PropTypes.bool,
});

export const tourAccommodationOptionGroupPropTypes = PropTypes.shape({
  day: PropTypes.number,
  defaultOption: PropTypes.oneOfType([hotelTourOptionPropTypes, motorhomeTourOptionPropTypes])
    .isRequired,
  key: PropTypes.string,
  subTitle: PropTypes.string,
  upgrades: PropTypes.arrayOf(
    PropTypes.oneOfType([hotelTourOptionPropTypes, motorhomeTourOptionPropTypes])
  ),
});

export const ticketOptionPropTypes = PropTypes.shape({
  category: PropTypes.string,
  fullDescription: PropTypes.string,
  itineraryDescription: PropTypes.string,
  key: PropTypes.string,
  title: PropTypes.string,
});

export const ticketTourOptionPropTypes = PropTypes.shape({
  key: PropTypes.string,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  option: ticketOptionPropTypes,
  pricePerPax: pricePerPaxPropTypes,
  soldOut: PropTypes.bool,
});

const tourTicketOptionGroupPropTypes = PropTypes.shape({
  day: PropTypes.number,
  defaultOption: ticketTourOptionPropTypes,
  key: PropTypes.string,
  matchKickOffDate: PropTypes.string,
  matchKickOffTime: PropTypes.string,
  stadium: stadiumPropTypes,
  subTitle: PropTypes.string,
  upgrades: PropTypes.arrayOf(ticketTourOptionPropTypes),
});

export const excursionOptionPropTypes = PropTypes.shape({
  key: PropTypes.string,
  title: PropTypes.string,
  fullDescription: PropTypes.string,
  itineraryDescription: PropTypes.string,
  image: imagePropTypes,
  pricePerPax: pricePerPaxPropTypes,
  time: PropTypes.string,
});

const tourExcursionOptionGroupPropTypes = PropTypes.shape({
  key: PropTypes.string,
  day: PropTypes.number,
  subTitle: PropTypes.string,
  options: PropTypes.arrayOf(excursionOptionPropTypes),
});

export const extraOptionPropTypes = PropTypes.shape({
  fullDescription: PropTypes.string,
  image: imagePropTypes,
  itineraryDescription: PropTypes.string,
  key: PropTypes.string,
  title: PropTypes.string,
});

export const stopoverOptionPropTypes = PropTypes.shape({
  destination: PropTypes.string,
  excursionOption: tourExcursionOptionGroupPropTypes,
  fullDescription: PropTypes.string,
  heroBannerTitleText: PropTypes.string,
  hotelOption: tourAccommodationOptionGroupPropTypes,
  images: PropTypes.arrayOf(imagePropTypes),
  itinerary: PropTypes.arrayOf(itineraryDay),
  itineraryDescription: PropTypes.string,
  key: PropTypes.string,
  modalContent: PropTypes.arrayOf(content),
  pricePerPax: pricePerPaxPropTypes,
  soldOut: PropTypes.bool,
  soldOutText: PropTypes.string,
});

const stopoverTourOptionData = PropTypes.shape({
  key: PropTypes.string,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityText: PropTypes.string,
  numberOfNights: PropTypes.number,
  pricePerPax: pricePerPaxPropTypes,
  soldOut: PropTypes.bool,
});

export const stopoverTourOptionPropTypes = PropTypes.shape({
  data: PropTypes.arrayOf(stopoverTourOptionData),
  option: stopoverOptionPropTypes,
  key: PropTypes.string,
});

export const tourStopoverOptionGroupPropTypes = PropTypes.shape({
  day: PropTypes.number,
  options: PropTypes.arrayOf(stopoverTourOptionPropTypes),
  stopoverType: PropTypes.string,
  key: PropTypes.string,
  subTitle: PropTypes.string,
});

export const labelsPropTypes = PropTypes.shape({
  accommodationTitle: PropTypes.string,
  addedToPackageText: PropTypes.string,
  atolProtectedText: PropTypes.string,
  closeModalText: PropTypes.string,
  durationText: PropTypes.string,
  enforceSingleAccommodationDescriptionText: PropTypes.string,
  enforceSingleAccommodationTitleText: PropTypes.string,
  extraTitle: PropTypes.string,
  excursionsDateText: PropTypes.string,
  excursionsTimeText: PropTypes.string,
  failedToRetrieveCheckoutProcessSettingsMessage: PropTypes.string,
  flightClassTitle: PropTypes.string,
  flightTitle: PropTypes.string,
  googleApiKey: PropTypes.string.isRequired,
  googleMapStyleId: PropTypes.string.isRequired,
  hotelViewDetailsButtonText: PropTypes.string,
  invalidPaymentOptionMessage: PropTypes.string,
  invalidTourIdMessage: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  miscellaneousExtrasTitle: PropTypes.string,
  motorhomeOverlayBerthLabelText: PropTypes.string,
  motorhomeOverlayGearboxLabelText: PropTypes.string,
  motorhomeOverlayTypeLabelText: PropTypes.string,
  SelectCampingTitleText: PropTypes.string,
  MotorhomeViewDetailsButtonText: PropTypes.string,
  overlayPriceFromLabelText: PropTypes.string,
  perPersonText: PropTypes.string,
  preferredCardImage: imagePropTypes,
  removeFromPacakgeText: PropTypes.string,
  roomsTitle: PropTypes.string,
  sessionInvalidTourIdMessage: PropTypes.string,
  singleRoomSupplementText: PropTypes.string,
  soldOutText: PropTypes.string,
  stadiumButtonText: PropTypes.string,
  stopoverViewDetailsButtonText: PropTypes.string,
  submitText: PropTypes.string,
  ticketTitle: PropTypes.string,
  transportTitle: PropTypes.string,
  validationNoRoomSelected: PropTypes.string,
  validationTooManyRoomsSelected: PropTypes.string,
  validationSelectMoreRooms: PropTypes.string,
  validationCompleteAllRooms: PropTypes.string,
  validationSelectMotorhome: PropTypes.string,
  validationSelectMotorhomeWithMoreBerths: PropTypes.string,
  validationNoMotorhomesAvailableForParty: PropTypes.string,
  validationSelectCamping: PropTypes.string,
  validationSelectCampingWithMoreBerths: PropTypes.string,
  validationNoCampingAvailableForParty: PropTypes.string,
  validationPaxCombinationNotSupported: PropTypes.string,
});

export const eventOptionsPackagePropTypes = PropTypes.shape({
  accommodationOptions: PropTypes.arrayOf(tourAccommodationOptionGroupPropTypes),
  allowIndividualTicketUpgrade: PropTypes.bool,
  costPerPerson: PropTypes.number,
  enforceAllRoomsFilledValidation: PropTypes.bool,
  enforceSingleAccommodationType: PropTypes.bool,
  errorCode: PropTypes.number,
  excursionOptions: PropTypes.arrayOf(tourExcursionOptionGroupPropTypes), // previously extraGroups
  extras: PropTypes.arrayOf(extraOptionPropTypes), // previously miscellaneousExtras
  image: imagePropTypes,
  isSoldOut: PropTypes.bool,
  itinerarySideBar: itinerarySideBarPropTypes,
  limitedAvailability: PropTypes.string,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
  maxPartySize: PropTypes.number,
  price: PropTypes.number, // previously costPerPerson
  selectedOptions: selectedOptionsPropTypes,
  singleRoomSupplementText: PropTypes.string,
  stopoverOptions: PropTypes.arrayOf(tourStopoverOptionGroupPropTypes),
  stopoverOptionsOptional: PropTypes.bool,
  ticketOptions: PropTypes.arrayOf(tourTicketOptionGroupPropTypes),
  title: PropTypes.string,
  tourFromDate: PropTypes.string,
  tourToDate: PropTypes.string,
  tourDescription: PropTypes.string, // description
  tournamentName: PropTypes.string, // previously tournament
  url: PropTypes.string,
  waitingListLink: PropTypes.string,
});

export const paxTypesPropTypes = PropTypes.shape({
  key: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  maximumAge: PropTypes.number,
  minimumAge: PropTypes.number.isRequired,
  minimumAddAge: PropTypes.number,
  requiredPaxTypeKeys: PropTypes.arrayOf(PropTypes.string),
  tourPlanPaxTypeCode: PropTypes.string,
  tourPlanPaxType: PropTypes.number,
});

export const paxRulePropType = PropTypes.shape({
  paxTypeKey: PropTypes.string.isRequired,
  minimumPaxCount: PropTypes.number.isRequired,
  maximumPaxCount: PropTypes.number.isRequired,
});

export const paxRulesPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    key: PropTypes.string.isRequired,
    paxRules: PropTypes.arrayOf(paxRulePropType).isRequired,
    minimumPartySize: PropTypes.number.isRequired,
    maximumPartySize: PropTypes.number.isRequired,
  })
);
